// Authenticated Tasks webform — C3.
// One-time task only — no recurrence selector. Posts via
// useOfflineRpcSubmit('task_submit') against the live submit_task_instance
// RPC, which validates submitted_by against the 'tasks-public' team
// availability and assignee against tasks_public_assignee_availability.
//
// NOT ANON, despite the '/webforms/tasks' alias and the "public" naming that
// survives in tasks_public_assignee_availability. Migration 193 revoked anon
// EXECUTE on list_eligible_assignees and dropped anon read on webform_config,
// and App's LoginScreen gate sits above WebformsEntry, so every visitor here
// is signed in.
//
// Submitted-by = roster display name string (filtered roster).
// Assignee     = profiles.id uuid (filtered eligible profiles).
// Roster IDs and profile UUIDs MUST NOT mix in the same hiddenIds array.
//
// The assignee picker is a RENDER INPUT, not a read this component performs:
// TasksWebformAuthenticatedBridge owns the tasks.webform.assignees resource and
// injects the already-filtered options. Do not reintroduce a Supabase read for
// them here — `sb` below is the To Do WRITE path only.
//
// Stable parent id is minted as 'ti-' + crypto.randomUUID() per the C3
// spec — useOfflineRpcSubmit's default ${formKind}-<ts>-<rand> doesn't
// match the task_instances PK convention.
import React from 'react';
import type {RpcSubmitState} from '../lib/useOfflineRpcSubmit.js';
import type {SessionSubmitter} from './webformsRoutes.js';
import {todayCentralISO} from '../lib/dateUtils.js';
import {useOfflineRpcSubmit} from '../lib/useOfflineRpcSubmit.js';
import {
  TODO_SECTIONS,
  createTodoItem,
  fireTodoChangeEvent,
  friendlyTodoError,
  generateTodoItemId,
  uploadTodoPhotos,
} from '../lib/todoApi.js';
import StuckSubmissionsModal, {payloadMember, payloadText} from './StuckSubmissionsModal.jsx';
import LockedSubmitter from './LockedSubmitter.jsx';
import {sb} from '../lib/supabase.js';
import {useQueuedRpcRecovery} from './useQueuedRpcRecovery.js';
import {recoveryText} from '../lib/offlineSubmissionRecovery.js';

function mintTiInstanceId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return 'ti-' + crypto.randomUUID().replace(/-/g, '');
  }
  return 'ti-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

// `catch (e)` binds `unknown` under strict TypeScript. This is the file's own
// `e && e.message ? e.message : String(e)` idiom, unchanged, with the narrowing
// that makes `.message` readable written once instead of at each of its sites.
function errorMessage(e: unknown): string {
  if (typeof e === 'object' && e !== null && 'message' in e && typeof e.message === 'string' && e.message) {
    return e.message;
  }
  return String(e);
}

const wfBg: React.CSSProperties = {
  minHeight: '100vh',
  background: 'var(--bg-page)',
  padding: '1rem',
  fontFamily: 'inherit',
};
const cardS: React.CSSProperties = {
  background: 'white',
  borderRadius: 12,
  padding: '20px',
  marginBottom: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,.08)',
};
const inpS: React.CSSProperties = {
  fontFamily: 'inherit',
  fontSize: 15,
  padding: '10px 12px',
  border: '1px solid var(--border-strong)',
  borderRadius: 12,
  width: '100%',
  outline: 'none',
  background: 'white',
  color: 'var(--ink)',
  boxSizing: 'border-box',
};
const lblS: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  color: 'var(--ink)',
  marginBottom: 5,
  fontWeight: 600,
};

export type TasksWebformAssigneeOption = {
  id: string;
  full_name: string | null;
};

/**
 * What the authenticated bridge injects. Every field is optional so the
 * presenter stays renderable in isolation, and `assigneeOptionsLoading`
 * defaults to TRUE: an un-bridged mount has not loaded anything, and
 * defaulting it to false would render "no eligible assignees" — a settled,
 * wrong answer — instead of a pending one.
 */
export type TasksWebformResourceProps = {
  sessionSubmitter?: SessionSubmitter;
  /** Authenticated route-resource output injected by the app-side bridge. */
  assigneeOptions?: readonly TasksWebformAssigneeOption[];
  assigneeOptionsLoading?: boolean;
  assigneeOptionsError?: string | null;
  retryAssigneeOptions?: () => Promise<unknown> | void;
};

type TasksWebformProps = TasksWebformResourceProps;

// 'task' writes a task_instances row through the offline RPC queue; 'todo'
// writes a todo_items row directly. Both branches of every conditional below
// key off exactly these two values.
type SubmissionType = 'task' | 'todo';

const TasksWebform = ({
  sessionSubmitter,
  assigneeOptions = [],
  assigneeOptionsLoading = true,
  assigneeOptionsError = null,
  retryAssigneeOptions,
}: TasksWebformProps) => {
  const today = React.useMemo(() => todayCentralISO(), []);

  const [submissionType, setSubmissionType] = React.useState<SubmissionType>('task');
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [dueDate, setDueDate] = React.useState(today);
  const [todoSection, setTodoSection] = React.useState('general');
  // Login-required form: submitter is locked to the signed-in user (no roster).
  const submittedBy = sessionSubmitter?.name || '';
  const [assignee, setAssignee] = React.useState('');
  const [photoFile, setPhotoFile] = React.useState<File | null>(null); // Optional File / Blob; one max
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState('');
  // 'none' plus whatever submit() reported. The To Do branch sets 'synced'
  // itself; RpcSubmitState is the RPC queue's own two-member union.
  const [doneState, setDoneState] = React.useState<RpcSubmitState | 'none'>('none');
  const [doneSubmissionType, setDoneSubmissionType] = React.useState<SubmissionType>('task');
  const [stuckOpen, setStuckOpen] = React.useState(false);
  // Stable to Do id per intent (batch a): the task branch keeps its parent id
  // stable across retries through the offline queue; the To Do branch must
  // not mint a fresh id per click, or a flaky-network retry creates a second
  // to do instead of replaying the first attempt. The id is minted when a
  // submission begins and reset when the composer empties (resetForm), so
  // 'Submit Another' starts a new intent. A definitive WCF01 refusal clears
  // it too (T3). The retry always sends the current photo set.
  const todoIdRef = React.useRef<string | null>(null);

  const recovery = useQueuedRpcRecovery('task_submit', {
    capture: () => ({
      submissionType,
      title,
      description,
      dueDate,
      todoSection,
      assignee,
      photoFile,
      doneState,
      doneSubmissionType,
    }),
    restore: (d) => {
      setSubmissionType(d.submissionType);
      setTitle(d.title);
      setDescription(d.description);
      setDueDate(d.dueDate);
      setTodoSection(d.todoSection);
      setAssignee(d.assignee);
      setPhotoFile(d.photoFile);
      setDoneState(d.doneState);
      setDoneSubmissionType(d.doneSubmissionType);
    },
    apply: (loaded) => {
      const p = loaded.record.args.parent_in as Record<string, unknown>;
      setSubmissionType('task');
      setTitle(recoveryText(p.title));
      setDescription(recoveryText(p.description));
      setDueDate(recoveryText(p.due_date));
      setAssignee(recoveryText(p.assignee_profile_id));
      setPhotoFile(loaded.photo);
      setDoneState('none');
    },
    error: setErr,
    opened: () => {
      setStuckOpen(false);
      setErr('');
    },
    externallyBusy: submitting,
  });
  const {submit, stuckRows, retryStuck, discardStuck} = useOfflineRpcSubmit('task_submit');
  const isTodoMode = submissionType === 'todo';
  const submissionLabel = isTodoMode ? 'To Do' : 'Task';
  // A Task needs a settled, successfully-filtered assignee list. A To Do has no
  // assignee, so it stays submittable while the picker is pending or failed.
  const taskAssigneeOptionsReady = !assigneeOptionsLoading && !assigneeOptionsError;

  const initialStuckShownRef = React.useRef(false);
  React.useEffect(() => {
    if (stuckRows.length > 0 && !initialStuckShownRef.current) {
      initialStuckShownRef.current = true;
      setStuckOpen(true);
    }
  }, [stuckRows.length]);

  function chooseSubmissionType(nextType: string) {
    if (recovery.loaded) return;
    if (nextType !== 'task' && nextType !== 'todo') return;
    setSubmissionType(nextType);
    setErr('');
    if (nextType === 'todo') {
      setDueDate('');
      setAssignee('');
    } else {
      setDueDate((d) => d || today);
    }
  }

  async function handleSubmit() {
    if (!isTodoMode && !taskAssigneeOptionsReady) {
      setErr(assigneeOptionsError || 'Form options are still loading.');
      return;
    }
    if (!title.trim()) {
      setErr('Title is required.');
      return;
    }
    if (isTodoMode && title.trim().length < 3) {
      setErr('Title must be at least 3 characters.');
      return;
    }
    if (!isTodoMode && !dueDate) {
      setErr('Due date is required.');
      return;
    }
    if (!isTodoMode && !submittedBy) {
      setErr('Submitted by is required.');
      return;
    }
    if (!isTodoMode && !assignee) {
      setErr('Assignee is required.');
      return;
    }
    if (!(await recovery.beginSubmit())) return;
    setErr('');
    setSubmitting(true);
    setDoneSubmissionType(submissionType);
    try {
      if (isTodoMode) {
        // One id per intent (batch a): minted on the first attempt of this
        // submission and kept across transport-only retries, so a retry hits
        // create_todo_item's replay contract with the same identity instead
        // of creating a duplicate to do. A definitive server refusal (WCF01)
        // also clears it (T3). resetForm clears it too. The retry always
        // sends the CURRENT photo set (T2 v2): combined with 238's photos-in-
        // canonical, a changed photo set gets a new intent via WCF01 + T3.
        if (!todoIdRef.current) todoIdRef.current = generateTodoItemId();
        const id = todoIdRef.current;
        const photoPaths = photoFile ? await uploadTodoPhotos(sb, id, 'origination', [photoFile]) : [];
        await createTodoItem(sb, {
          id,
          title: title.trim(),
          description: description.trim() || null,
          section: todoSection,
          dueDate: dueDate || null,
          photoPaths,
        });
        fireTodoChangeEvent();
        setDoneState('synced');
        return;
      }
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        due_date: dueDate,
        assignee_profile_id: assignee,
        submitted_by_team_member: submittedBy,
      };
      const result = await submit(payload, {
        ...recovery.options,
        parentId: mintTiInstanceId(),
        photo: photoFile || null,
      });
      if (!recovery.finish(true)) setDoneState(result.state);
    } catch (e) {
      // T3: a definitive server refusal (WCF01 — the composed intent can
      // never land under this id) ends the intent, so the next click starts
      // a fresh submission instead of dead-ending. Transport failures keep
      // the id: the retry must still replay the original attempt.
      if (isTodoMode && (e as {code?: unknown})?.code === 'WCF01') {
        todoIdRef.current = null;
      }
      const msg = isTodoMode ? friendlyTodoError(e) : errorMessage(e);
      setErr('Could not submit: ' + msg);
    } finally {
      recovery.finish();
      setSubmitting(false);
    }
  }

  function resetForm() {
    setTitle('');
    setDescription('');
    setDueDate(submissionType === 'todo' ? '' : today);
    setAssignee('');
    setTodoSection('general');
    setPhotoFile(null);
    setErr('');
    setDoneState('none');
    // The composer is empty again: the next submission is a NEW intent.
    todoIdRef.current = null;
  }

  const logoEl = (
    <div style={{textAlign: 'center', marginBottom: 20}}>
      <div style={{fontSize: 18, fontWeight: 800, color: 'var(--brand)', letterSpacing: -0.3}}>WCF Planner</div>
      <div style={{fontSize: 12, color: 'var(--ink)', marginTop: 2}}>Submit a Task or a To Do</div>
    </div>
  );

  const doneLabel = doneSubmissionType === 'todo' ? 'To Do' : 'Task';

  if (doneState !== 'none')
    return (
      <div style={wfBg}>
        {recovery.banner}
        <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '2rem', textAlign: 'center'}}>
          {logoEl}
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: doneState === 'queued' ? 'var(--warn-ink)' : 'var(--ink)',
              marginBottom: 8,
            }}
          >
            {doneState === 'queued' ? 'Saved on this device' : `${doneLabel} submitted!`}
          </div>
          {doneState === 'queued' && (
            <div
              data-submit-state="queued"
              style={{
                fontSize: 12,
                color: 'var(--text-2)',
                marginBottom: 12,
                lineHeight: 1.5,
                background: 'var(--warn-soft)',
                border: '1px solid var(--warn-stripe)',
                borderRadius: 12,
                padding: '8px 12px',
                textAlign: 'left',
              }}
            >
              No connection right now. Your task will sync as soon as the device is back online.
            </div>
          )}
          {doneState === 'synced' && (
            <div data-submit-state="synced" style={{display: 'none'}}>
              synced
            </div>
          )}
          <button
            onClick={resetForm}
            className="wcf-clickable wcf-btn wcf-btn--primary"
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 999,
              border: 'none',
              background: 'var(--brand)',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 10,
            }}
          >
            Submit Another {doneLabel}
          </button>
          <button
            onClick={() => {
              window.location.href = '/dailys';
            }}
            className="wcf-clickable wcf-btn wcf-btn--secondary"
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 999,
              border: '1px solid var(--border-strong)',
              background: 'white',
              color: 'var(--ink)',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Done
          </button>
        </div>
      </div>
    );

  return (
    <div style={wfBg}>
      {recovery.banner}
      <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '1rem'}}>
        {logoEl}

        {stuckRows.length > 0 && (
          <button
            onClick={() => setStuckOpen(true)}
            data-stuck-button="1"
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid var(--warn-stripe)',
              background: 'var(--warn-soft)',
              color: 'var(--warn-ink)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 12,
              textAlign: 'left',
            }}
          >
            {stuckRows.length} unsynced task submission{stuckRows.length === 1 ? '' : 's'} — tap to review
          </button>
        )}

        <div data-submit-kind-toggle="1" style={cardS}>
          <label style={lblS}>Submit type</label>
          <div
            role="group"
            aria-label="Submit type"
            style={{display: 'flex', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-strong)'}}
          >
            {[
              {key: 'task', label: 'Task'},
              {key: 'todo', label: 'To Do'},
            ].map((opt, i) => {
              const selected = submissionType === opt.key;
              return (
                <React.Fragment key={opt.key}>
                  {i > 0 && <div style={{width: 1, background: 'var(--border-strong)', flexShrink: 0}} />}
                  <button
                    type="button"
                    data-submit-kind={opt.key}
                    aria-pressed={selected}
                    onClick={() => chooseSubmissionType(opt.key)}
                    style={{
                      flex: 1,
                      padding: '10px 0',
                      border: 'none',
                      fontFamily: 'inherit',
                      fontSize: 13,
                      fontWeight: selected ? 700 : 500,
                      cursor: 'pointer',
                      background: selected ? 'var(--brand)' : 'white',
                      color: selected ? 'white' : 'var(--ink)',
                    }}
                  >
                    {opt.label}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div style={cardS}>
          <label style={lblS}>{submissionLabel} title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs doing?"
            style={inpS}
          />
        </div>

        <div style={cardS}>
          <label style={lblS}>Details</label>
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{...inpS, resize: 'vertical', fontFamily: 'inherit'}}
          />
        </div>

        <div style={cardS}>
          <label style={lblS}>Due date{isTodoMode ? ' (optional)' : ' *'}</label>
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={inpS} />
        </div>

        {isTodoMode && (
          <div style={cardS}>
            <label style={lblS}>Section</label>
            <select value={todoSection} onChange={(e) => setTodoSection(e.target.value)} style={inpS}>
              {TODO_SECTIONS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {!isTodoMode && (
          <>
            <div style={cardS}>
              <LockedSubmitter name={submittedBy} label="Submitted by *" labelStyle={lblS} />
            </div>

            <div style={cardS}>
              <label style={lblS}>Assign to *</label>
              <select
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                disabled={assigneeOptionsLoading || Boolean(assigneeOptionsError)}
                style={inpS}
              >
                <option value="">Choose…</option>
                {assigneeOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name || p.id.slice(0, 8)}
                  </option>
                ))}
              </select>
              {assigneeOptionsLoading && (
                <div data-assignee-options-loading="1" style={{fontSize: 11, color: 'var(--ink-muted)', marginTop: 6}}>
                  Loading eligible assignees…
                </div>
              )}
              {assigneeOptionsError && (
                <div
                  data-assignee-options-error="1"
                  style={{
                    fontSize: 11,
                    color: 'var(--danger-ink)',
                    marginTop: 6,
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <span>{assigneeOptionsError}</span>
                  {retryAssigneeOptions && (
                    <button
                      type="button"
                      onClick={() => {
                        void retryAssigneeOptions();
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--brand)',
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      Try again
                    </button>
                  )}
                </div>
              )}
              {!assigneeOptionsLoading && !assigneeOptionsError && assigneeOptions.length === 0 && (
                <div style={{fontSize: 11, color: 'var(--warn-ink)', marginTop: 6}}>
                  No eligible assignees available. Ask an admin to enable a planner user for public tasks.
                </div>
              )}
            </div>
          </>
        )}

        <div style={cardS}>
          <label style={lblS}>Photo</label>
          {photoFile ? (
            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
              <span style={{fontSize: 12, color: 'var(--ink)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                {photoFile.name || 'photo.jpg'} ({Math.round((photoFile.size || 0) / 1024)} KB)
              </span>
              <button
                type="button"
                onClick={() => setPhotoFile(null)}
                className="wcf-clickable wcf-btn wcf-btn--secondary"
                style={{
                  background: '#fff',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 999,
                  padding: '10px 16px',
                  fontSize: 13,
                  color: 'var(--ink)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (f) setPhotoFile(f);
              }}
              style={{...inpS, padding: '8px 10px'}}
            />
          )}
          <div style={{fontSize: 11, color: 'var(--ink-muted)', marginTop: 4}}>
            One photo max. Compressed before upload.
          </div>
        </div>

        {err && (
          <div
            style={{
              color: 'var(--danger-ink)',
              fontSize: 13,
              marginBottom: 10,
              padding: '8px 12px',
              background: 'var(--danger-soft)',
              borderRadius: 12,
            }}
          >
            {err}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting || (!isTodoMode && !taskAssigneeOptionsReady)}
          className="wcf-clickable wcf-btn wcf-btn--primary"
          style={{
            width: '100%',
            padding: 14,
            border: 'none',
            borderRadius: 999,
            background: 'var(--brand)',
            color: 'white',
            fontSize: 15,
            fontWeight: 600,
            cursor: submitting ? 'default' : 'pointer',
            opacity: submitting ? 0.45 : 1,
            fontFamily: 'inherit',
            marginBottom: 16,
          }}
        >
          {submitting ? 'Submitting…' : `Submit ${submissionLabel}`}
        </button>

        <div style={{textAlign: 'center', marginTop: 12}}>
          <button
            onClick={() => {
              window.location.href = '/dailys';
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--brand)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              textDecoration: 'underline',
            }}
          >
            {'‹ Back to Webforms'}
          </button>
        </div>
      </div>

      {stuckOpen && (
        <StuckSubmissionsModal
          onOpen={recovery.open}
          busy={submitting || recovery.opening}
          error={err}
          rows={stuckRows}
          formLabel="Task submission"
          describeRow={(row) => {
            const parent = payloadMember(payloadMember(row.record, 'args'), 'parent_in');
            const t = payloadText(parent, 'title');
            const d = payloadText(parent, 'due_date');
            return `${t} · due ${d} (not yet sent)`;
          }}
          onRetry={async (csid) => {
            await retryStuck(csid);
          }}
          onDiscard={async (csid) => {
            await discardStuck(csid);
          }}
          onClose={() => setStuckOpen(false)}
        />
      )}
    </div>
  );
};

export default TasksWebform;
